import React, { useMemo } from "react";
import { Select, MultiSelect, TextInput, NumberInput } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import moment from "moment";
import { getMcaWatchFieldMeta } from "../../helpers/mcaWatchFieldMeta";
import { coerceWatchFieldValueForSelect } from "../../helpers/notificationWatchFieldValueOptions";
import McaConditionRefValueInput from "./McaConditionRefValueInput";

const splitListValue = (value) =>
  String(value ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const buildSelectData = (options, currentValue) => {
  const data = [...(options ?? [])];
  if (
    currentValue !== "" &&
    currentValue != null &&
    !data.some((option) => option.value === String(currentValue))
  ) {
    data.push({ value: String(currentValue), label: String(currentValue) });
  }
  return data;
};

const McaConditionValueInput = ({
  label = "Value",
  entityKey,
  field,
  conditionType,
  value,
  onChange,
  disabled = false,
}) => {
  const meta = useMemo(
    () => getMcaWatchFieldMeta(entityKey, field),
    [entityKey, field]
  );

  if (!conditionType) {
    return (
      <TextInput
        label={label}
        placeholder="Select a condition first"
        value=""
        disabled
      />
    );
  }

  const { valueType, options, refConfig } = meta;

  if (valueType === "ref" && refConfig) {
    return (
      <McaConditionRefValueInput
        label={label}
        refConfig={refConfig}
        conditionType={conditionType}
        value={value}
        onChange={onChange}
        disabled={disabled || !field}
      />
    );
  }

  if (valueType === "boolean" && conditionType === "$eq") {
    const selectValue = coerceWatchFieldValueForSelect(value, options);
    return (
      <Select
        label={label}
        placeholder="Select value"
        data={options ?? []}
        value={selectValue || null}
        onChange={(next) => onChange(next ?? "")}
        disabled={disabled || !field}
        withinPortal
        clearable
      />
    );
  }

  if (valueType === "enum" && conditionType === "$in") {
    const selected = splitListValue(value);
    const data = buildSelectData(options, null);
    return (
      <MultiSelect
        label={label}
        placeholder="Select one or more values"
        data={data}
        value={selected}
        onChange={(next) => onChange((next ?? []).join(", "))}
        disabled={disabled || !field}
        searchable
        clearable
        withinPortal
      />
    );
  }

  if (valueType === "enum" && conditionType === "$eq") {
    const selectValue = coerceWatchFieldValueForSelect(value, options);
    return (
      <Select
        label={label}
        placeholder="Select value"
        data={buildSelectData(options, selectValue)}
        value={selectValue || null}
        onChange={(next) => onChange(next ?? "")}
        disabled={disabled || !field}
        searchable
        clearable
        withinPortal
      />
    );
  }

  if (valueType === "number") {
    const numericValue =
      value === "" || value == null ? "" : Number.isNaN(Number(value)) ? value : Number(value);
    return (
      <NumberInput
        label={label}
        placeholder="e.g. 100000"
        value={numericValue}
        onChange={(next) => onChange(next == null ? "" : String(next))}
        disabled={disabled || !field}
        hideControls
        precision={undefined}
      />
    );
  }

  if (valueType === "date") {
    const dateValue =
      value && moment(value, moment.ISO_8601, true).isValid()
        ? moment(value).toDate()
        : value
          ? moment(value).toDate()
          : null;
    return (
      <DatePicker
        label={label}
        placeholder="Pick date"
        value={dateValue}
        onChange={(next) =>
          onChange(next ? moment(next).format("YYYY-MM-DD") : "")
        }
        disabled={disabled || !field}
        allowFreeInput
        withinPortal
      />
    );
  }

  if (conditionType === "$in") {
    return (
      <TextInput
        label={label}
        placeholder="Comma-separated values"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || !field}
      />
    );
  }

  return (
    <TextInput
      label={label}
      placeholder="e.g. INACTIVE or 100000"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || !field}
    />
  );
};

export default McaConditionValueInput;
